/**
 * v5.7.77 — Sentry error tracking + performance for the React app.
 *
 * No-op when `REACT_APP_SENTRY_DSN` is not set so this scaffold can
 * land before you have a DSN. Set it later in `frontend/.env` and
 * rebuild — no code changes needed.
 *
 * Setup:
 *   1. Create a Sentry project (platform: React) at sentry.io.
 *   2. Copy the DSN from Project Settings → Client Keys (DSN).
 *   3. Set in `frontend/.env`:
 *        REACT_APP_SENTRY_DSN="https://<key>@sentry.io/<project_id>"
 *        REACT_APP_SENTRY_ENVIRONMENT="production"
 *        REACT_APP_SENTRY_TRACES_SAMPLE_RATE="0.1"
 *   4. Rebuild the frontend (CRA inlines REACT_APP_* at build time).
 */
import * as Sentry from '@sentry/react';

const dsn = process.env.REACT_APP_SENTRY_DSN;
// 2026-07 — runtime hostname check: preview-pod sessions (agent testing,
// HeadlessChrome sweeps) tag as env=preview so they never pollute the
// production issue stream (e.g. FWS-FRONTEND-4 came from a preview test run
// but landed under env=production). Prod domains keep the build-time env.
const isPreviewHost = typeof window !== 'undefined' && /\.preview\.emergentagent\.com$/.test(window.location.hostname);
const environment = isPreviewHost ? 'preview' : (process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development');
const tracesSampleRate = Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.0');

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    tracesSampleRate,
    // Do NOT send default PII — we attach user.id explicitly after login
    // via `Sentry.setUser(...)` and want full control of what leaves the bundle.
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration({
        // Trace propagation: only attach `sentry-trace` headers to our own
        // API so we don't leak trace IDs to 3rd-party domains (analytics, etc.).
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/.*\.preview\.emergentagent\.com\/api/,
          /^https:\/\/api\.fantasyworldstar\.com/,
        ],
      }),
    ],
    beforeSend(event) {
      // Drop noisy ResizeObserver / network-aborted errors — these
      // happen constantly in normal browser usage and aren't bugs.
      const exc = event?.exception?.values?.[0];
      const msg = exc?.value || event?.message || '';
      if (/ResizeObserver loop|Non-Error promise rejection captured|AbortError/i.test(msg)) {
        return null;
      }
      return event;
    },
  });
  // Surface a marker so devs know in console that Sentry is live.
  // eslint-disable-next-line no-console
  console.info('[Sentry] initialized · env=' + environment + ' · traces=' + tracesSampleRate);
} else {
  // eslint-disable-next-line no-console
  console.info('[Sentry] disabled (REACT_APP_SENTRY_DSN not set).');
}

export { Sentry };

/**
 * Tag the active user on all subsequent Sentry events. Call right
 * after `/api/auth/login` succeeds; clear by passing `null` on logout.
 */
export function setSentryUser(userIdOrNull, username) {
  if (!dsn) return;
  if (!userIdOrNull) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: userIdOrNull, username });
}

export function captureMessage(message, level = 'info', context) {
  if (!dsn) return;
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    if (context && typeof context === 'object') {
      Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
    }
    Sentry.captureMessage(message);
  });
}

export function captureException(error, context) {
  if (!dsn) return;
  Sentry.withScope((scope) => {
    if (context && typeof context === 'object') {
      Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
    }
    Sentry.captureException(error);
  });
}
